import { Link, Outlet, useLocation, Navigate, useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  ShoppingBag,
  Wine,
  Package,
  CalendarRange,
  FileText,
  Settings,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  Inbox,
  BarChart3,
  Wallet,
  Menu,
  X,
  LayoutTemplate,
  Sparkles,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { goatAIService } from "@/services/goat-ai/goat-ai-service";
import {
  enablePushNotifications,
  getPushNotificationState,
  syncPushRegistration,
  sendTestPushNotification,
  type PushNotificationState,
} from "@/lib/push-notifications";
import logo from "@/assets/goatbar-logo.png";
import { googleCalendarService } from "@/services/google-calendar/google-calendar-service";

const nav: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
}[] = [
  { to: "/gia", label: "GIA", icon: Sparkles, exact: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/vendas", label: "Vendas", icon: ShoppingBag },
  { to: "/drinks", label: "Drinks", icon: Wine },
  { to: "/inventario", label: "Inventário", icon: Package },
  { to: "/eventos", label: "Eventos", icon: CalendarRange },
  { to: "/controladoria", label: "Controladoria", icon: BarChart3 },
  { to: "/contratos", label: "Contratos", icon: FileText },
  { to: "/modelos", label: "Modelos de Proposta", icon: LayoutTemplate },
  { to: "/configuracoes", label: "Configurações", icon: Settings },
];

export function AppShell({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const { loading, user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingAiCount, setPendingAiCount] = useState<number>(0);
  const [pushState, setPushState] = useState<PushNotificationState>("loading");
  const [pushActivating, setPushActivating] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushTesting, setPushTesting] = useState(false);
  const visibleNav = nav;

  useEffect(() => {
    if (!user) return;
    // Reconcilia eventos confirmados marcados como pendentes/erro, inclusive quando o status mudou fora desta tela.
    googleCalendarService.syncAllConfirmed().catch((error) => console.warn("[calendar-reconcile]", error));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    goatAIService.getPendingCount().then((cnt) => setPendingAiCount(cnt)).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    const handleRead = () => setPendingAiCount((count) => Math.max(0, count - 1));
    window.addEventListener("budget-notification-read", handleRead);
    return () => window.removeEventListener("budget-notification-read", handleRead);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setPushState("loading");
      return;
    }

    const refreshPushState = async () => {
      try {
        await syncPushRegistration().catch(() => {});
        const state = await getPushNotificationState();
        if (!cancelled) setPushState(state);
      } catch {
        if (!cancelled) setPushState("error");
      }
    };

    void refreshPushState();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleEnablePush = async () => {
    setPushActivating(true);
    setPushMessage(null);

    try {
      await enablePushNotifications();
      setPushState("enabled");
    } catch (error) {
      const state = await getPushNotificationState().catch(() => "error" as const);
      setPushState(state);
      setPushMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível ativar as notificações.",
      );
    } finally {
      setPushActivating(false);
    }
  };

  const handleTestPush = async () => {
    setPushTesting(true);
    setPushMessage(null);
    try {
      await sendTestPushNotification();
      setPushMessage("Teste enviado. A notificação deve aparecer neste iPhone.");
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : "Não foi possível enviar o teste.");
    } finally {
      setPushTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const initials = (user?.email ?? "GB").slice(0, 2).toUpperCase();
  const displayName = user?.email?.split("@")[0] ?? "Gestor";
  return (
    <div className="flex h-[100dvh] w-full min-w-0 max-w-[100vw] flex-col overflow-hidden bg-background text-foreground md:flex-row">
      {/* MOBILE TOPBAR */}
      <div className="shrink-0 flex items-center justify-between border-b border-border bg-surface px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))] md:hidden">
        <Link to="/gia" className="flex items-center gap-3">
          <img src={logo} alt="GOAT BAR" className="h-8 w-auto" />
          <div className="font-display text-[11px] font-semibold tracking-[0.18em] leading-none">
            GOAT BAR
          </div>
        </Link>
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 text-muted-foreground hover:text-foreground"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* MOBILE MENU DRAWER */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="relative w-72 max-w-[80vw] h-full bg-sidebar border-r border-sidebar-border shadow-2xl flex flex-col animate-in slide-in-from-left pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-6 pt-[calc(1.75rem+env(safe-area-inset-top))] pb-8 shrink-0">
              <Link
                to="/gia"
                className="flex items-center gap-3"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img src={logo} alt="GOAT BAR" className="h-10 w-auto" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-3 mb-2 label-eyebrow shrink-0">Operação</div>
            <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
              {nav.map((item) => {
                const Icon = item.icon;
                const active = item.exact
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to as any}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow-[0_4px_20px_-8px_var(--primary)]"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                    <span className="font-medium text-base flex-1">{item.label}</span>
                    {item.to === "/gia" && pendingAiCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500 text-black">
                        {pendingAiCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="m-3 p-3 rounded-xl bg-sidebar-accent border border-sidebar-border shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-primary-foreground font-display font-semibold text-xs">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate capitalize">{displayName}</div>
                </div>
                <button
                  onClick={signOut}
                  className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/40 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-sidebar border-r border-sidebar-border h-[100dvh] overflow-y-auto">
        <div className="px-6 pt-7 pb-8 shrink-0">
          <Link to="/gia" className="flex items-center gap-3">
            <img src={logo} alt="GOAT BAR" className="h-12 w-auto" />
            <div>
              <div className="font-display text-[13px] font-semibold tracking-[0.18em] leading-none">
                GOAT BAR
              </div>
              <div className="label-eyebrow mt-1.5 leading-none">Management</div>
            </div>
          </Link>
        </div>

        <div className="px-3 mb-2 label-eyebrow shrink-0">Operação</div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto min-h-0">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to as any}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? "bg-primary text-primary-foreground shadow-[0_4px_20px_-8px_var(--primary)]"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground"
                }`}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                <span className="font-medium flex-1">{item.label}</span>
                {item.to === "/gia" && pendingAiCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-black">
                    {pendingAiCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User card */}
        <div className="m-3 p-3 rounded-xl bg-sidebar-accent border border-sidebar-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-primary-foreground font-display font-semibold text-xs">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate capitalize">{displayName}</div>
              <div className="text-[11px] text-muted-foreground truncate">Gestor · Goat Bar</div>
            </div>
            <button
              onClick={signOut}
              title="Sair"
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/40 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex w-full min-w-0 max-w-[100vw] flex-1 flex-col overflow-y-auto overflow-x-hidden pb-[calc(5.25rem+env(safe-area-inset-bottom))] md:pb-0">
        {pushState === "enabled" ? (
          <div className="md:hidden px-4 pt-3">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5 shadow-sm">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-foreground">Notificações ativas</div>
                {pushMessage && (
                  <div className="truncate text-[10px] text-muted-foreground">{pushMessage}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => void handleTestPush()}
                disabled={pushTesting}
                className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-[10px] font-semibold text-foreground disabled:opacity-60"
              >
                {pushTesting ? "Enviando..." : "Testar"}
              </button>
            </div>
          </div>
        ) : pushState !== "loading" && pushState !== "unsupported" ? (
          <div className="md:hidden px-4 pt-3">
            <div className="rounded-xl border border-border bg-surface p-3.5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Bell className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">Notificações no iPhone</div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {pushState === "needs_install"
                      ? "Adicione o Goat Bar à Tela de Início pelo Safari para receber alertas push."
                      : pushState === "denied"
                        ? "As notificações estão bloqueadas. Libere a permissão nos Ajustes do iPhone para o Goat Bar."
                        : "Ative para receber alertas de orçamentos e contratos mesmo com o app fechado."}
                  </p>
                  {pushMessage && (
                    <p className="mt-2 text-xs text-destructive">{pushMessage}</p>
                  )}
                  {(pushState === "prompt" || pushState === "error") && (
                    <button
                      type="button"
                      onClick={() => void handleEnablePush()}
                      disabled={pushActivating}
                      className="mt-3 inline-flex items-center rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-60"
                    >
                      {pushActivating ? "Ativando..." : "Ativar notificações"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {children ?? <Outlet />}
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
        <div className="grid grid-cols-5 px-1 pt-1.5 pb-[calc(0.4rem+env(safe-area-inset-bottom))]">
          {nav.filter((item) => ["/gia", "/dashboard", "/vendas", "/eventos", "/controladoria"].includes(item.to)).map((item) => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={`bottom-${item.to}`}
                to={item.to as any}
                className={`flex min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-1.5 text-[9px] ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-[18px] w-[18px]" />
                <span className="w-full whitespace-nowrap text-center leading-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: string;
  breadcrumb?: ReactNode;
  action?: ReactNode;
  periodo?: ReactNode;
}

export function PageHeader({ title, subtitle, breadcrumb, action, periodo }: PageHeaderProps) {
  return (
    <header className="bg-surface/50 lg:bg-transparent lg:topbar-glass lg:sticky lg:top-0 z-30 border-b border-border lg:border-none">
      <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center lg:gap-6 lg:px-8 lg:py-5">
        <div className="min-w-0 flex-1">
          {breadcrumb && <div className="label-eyebrow mb-1 lg:mb-2">{breadcrumb}</div>}
          <h1 className="font-display text-xl font-semibold leading-tight lg:text-2xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-xs text-muted-foreground lg:text-sm">{subtitle}</p>
          )}
        </div>

        {action && <div className="w-full min-w-0 lg:hidden">{action}</div>}

        <div className="hidden lg:flex items-center gap-3">
          {periodo ?? (
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-surface text-sm hover:border-border-strong transition-colors">
              <span className="label-eyebrow !text-foreground">Últimos 30 dias</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}

          <button className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-surface hover:border-border-strong transition-colors">
            <Search className="h-4 w-4 text-muted-foreground" />
          </button>

          <NotificationsDropdown />
          {action}
        </div>
      </div>
    </header>
  );
}

function NotificationsDropdown() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  const loadNotifications = async () => {
    try {
      const list = await goatAIService.listBudgetNotifications();
      setItems(list);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const pendingCount = items.filter((i) => !i.is_read).length;

  const markRead = async (itemId: string) => {
    const item = items.find((candidate) => candidate.id === itemId);
    if (!item || item.is_read) return;
    setItems((current) => current.map((candidate) =>
      candidate.id === itemId ? { ...candidate, is_read: true } : candidate
    ));
    window.dispatchEvent(new Event("budget-notification-read"));
    try {
      await goatAIService.markBudgetNotificationRead(itemId);
    } catch {
      setItems((current) => current.map((candidate) =>
        candidate.id === itemId ? { ...candidate, is_read: false } : candidate
      ));
      window.dispatchEvent(new Event("budget-notifications-refresh"));
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          if (!open) loadNotifications();
        }}
        className="relative h-10 w-10 inline-flex items-center justify-center rounded-lg border border-border bg-surface hover:border-border-strong transition-colors cursor-pointer"
        aria-label="Abrir notificações"
      >
        <Bell className="h-4 w-4 text-muted-foreground" />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center animate-pulse">
            {pendingCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-full right-0 mt-2 w-84 bg-surface border border-border rounded-xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-top-2 fade-in">
            <div className="p-4 border-b border-border bg-background/50 flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                Notificações
                {pendingCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/20 text-primary">
                    {pendingCount} nova{pendingCount > 1 ? "s" : ""}
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={loadNotifications}
                className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
              >
                Atualizar
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-border/40">
              {items.length === 0 ? (
                <div className="p-8 flex flex-col items-center justify-center text-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Inbox className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Nenhuma notificação</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Você ainda não possui notificações pendentes.
                  </p>
                </div>
              ) : (
                items.map((item) => {
                  const data = item.structured_data || {};
                  const eventId = item.matched_event_id || data.event_id;
                  const clientName = data.client_name || item.source_sender_name || "Cliente";
                  const kind = data.type;
                  const isBudget = kind === "new_budget_request";
                  const notificationTitles: Record<string, string> = {
                    new_budget_request: "Novo orçamento recebido",
                    contract_data_received: "Dados do contrato recebidos",
                    contract_client_signed: "Cliente assinou o contrato",
                    contract_fully_signed: "Contrato totalmente assinado",
                  };
                  const notificationTitle = notificationTitles[kind] || "Notificação do evento";
                  const eventName = data.event_name || data.event_type || item.events?.event_name || "Evento";
                  const guests = isBudget ? data.guests : null;
                  const date = isBudget ? data.date : null;
                  const phone = isBudget ? data.phone || item.source_sender_id : null;

                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        void markRead(item.id);
                        setOpen(false);
                        if (eventId) void navigate({ to: `/eventos/${eventId}` as any });
                      }}
                      className="p-3.5 hover:bg-surface-hover/80 transition-colors flex flex-col gap-1.5 text-xs"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-foreground truncate flex items-center gap-1.5">
                          {!item.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                          {notificationTitle}
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0 font-medium">
                          {eventName}
                        </span>
                      </div>

                      <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                        <span>👤 {clientName}</span>
                        {date && <span>📅 {date}</span>}
                        {guests && <span>👥 {guests} pax</span>}
                        {phone && <span>📞 {phone}</span>}
                      </div>

                      {eventId && (
                        <Link
                          to={`/eventos/${eventId}` as any}
                          onClick={(event) => {
                            event.stopPropagation();
                            void markRead(item.id);
                            setOpen(false);
                          }}
                          className="mt-1 text-[11px] font-semibold text-primary hover:underline flex items-center gap-1 w-fit"
                        >
                          Abrir evento no sistema →
                        </Link>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
